import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { storeDispatch } from '../../../shared/model/dispatch'
import { feedbackCleared, FEEDBACK_SCOPES } from '../../../shared/model/feedbackSlice'
import { ridKey } from '../../../shared/model/keys'
import {
  cashierVerifyRequested,
  linkCheckRequested,
  operationCheckRequested,
  operationReconcileRequested,
  registerWorkRequested,
  rmkReloadRequested,
  sessionCloseForeignRequested,
  sessionCloseRequested,
  sessionPriceTypeRequested,
  sessionTakeoverRequested,
  shiftAdoptRequested,
  shiftCommandRequested,
  shiftOperationResolveRequested,
  workplaceMoveRequested,
} from './commands'
import { operationDismissed } from './operationsSlice'
import { moveWayOf } from './moveWay'
import { ownCashierSignInRequested } from './ownSignIn'
import { priceTypeRefOf } from './selectors'
import { cashierChosen, startPriceTypeChosen, startRegisterChosen } from './startSlice'
import type { RmkActions, RmkVm } from './types'

/**
 * Переходы окна РМК — их даёт binding (окна — забота оболочки, не пакета). Рабочие окна (плитки источников,
 * чеки, инциденты) открываются переходом на их адрес, а не поверх экрана.
 */
export interface RmkNavigation {
  /** «Другой источник» — плитки источников, без автоперехода. */
  switchSource: () => void
  openJournal: (deviceId: number) => void
  closeJournal: () => void
  openIncidents: () => void
  openPrintSettings?: () => void
  openReceipts: () => void
  /** «Действия» → «Возврат» → «Стандартный возврат» (RP-FB74): нет права `refunds.manage` - не задан, плитки нет. */
  openRefundSearch?: () => void
  /** «Действия» → «Возврат» → «Возврат по слип-чеку» (RP-FB74). */
  openSlipRefund?: () => void
}

/**
 * Действия экрана РМК → команды. Версии и идентификаторы берутся из vm — из
 * того, что человек ВИДИТ: служба отвергнет команду по устаревшей версии, и
 * человек увидит отказ, а не тихую перезапись.
 */
export function createRmkActions(vm: RmkVm, dispatch: Dispatch<UnknownAction>, navigation: RmkNavigation): RmkActions {
  const resourceId = vm.resourceId
  const mine = vm.mine
  const operation = vm.operation
  const ownSession = () =>
    mine === null
      ? null
      : {
          resourceId,
          deviceId: mine.deviceId,
          registerKey: mine.registerKey,
          sessionId: mine.sessionId,
          expectedVersion: mine.version,
        }

  return {
    reload: () => dispatch(rmkReloadRequested({ resourceId, deviceId: mine?.deviceId ?? null })),
    // «Открыть смену» / «Продолжить смену»: кассир, подтверждение кода, вид цены и касса уже выбраны в «Начале работы».
    startWork: () => {
      const start = vm.start
      const row = start.registers.selected
      if (row === null || !start.canStart || vm.busy) return
      const cashier = start.cashier
      dispatch(
        registerWorkRequested({
          resourceId,
          deviceId: row.deviceId,
          mode: start.action,
          ...(cashier?.kind === 'cashier'
            ? { cashierId: cashier.id, cashierName: cashier.name, cashierNeedsCode: true }
            : cashier?.kind === 'own'
              ? { asSelf: true }
              : {}),
          priceType: priceTypeRefOf(start.priceTypeId),
          priceTypeId: start.priceTypeId,
          registerName: row.name,
        }),
      )
    },
    chooseCashier: (cashierId) => {
      const option = vm.start.cashiers.options.find((item) => item.id === cashierId)
      if (option === undefined || option.selected) return
      dispatch(cashierChosen({ resourceId, cashier: { kind: 'cashier', id: option.id, name: option.name } }))
    },
    chooseOwnName: () => {
      if (vm.start.cashiers.empty) dispatch(cashierChosen({ resourceId, cashier: { kind: 'own' } }))
    },
    verifyCode: (credential) => {
      const code = vm.start.code
      if (code === null || credential.trim() === '') return
      if (code.state === 'verified' || code.state === 'checking' || code.state === 'unsupported') return
      void storeDispatch(dispatch)(cashierVerifyRequested({ resourceId, cashierId: code.cashierId, credential }))
    },
    signInOwn: (cashierId, credential) => {
      const worker = vm.ownCashiers?.items.find((item) => item.cashierId === cashierId)
      if (worker === undefined) return Promise.resolve({ ok: false as const, message: 'Выберите кассира.', lockedUntil: null })
      return storeDispatch(dispatch)(ownCashierSignInRequested({ resourceId, worker, credential }))
    },
    moveHere: (credential, cabinetCode) => {
      if (mine === null) return Promise.resolve(null)
      // К-120: чей код проверять — по связи кассира работы; пока список кассиров нашей системы не прочитан, код не отправляется.
      const way = moveWayOf(vm)
      if (way.kind === 'reading' || way.kind === 'failed') {
        return Promise.resolve('Список кассиров нашей системы не прочитан: повторите чтение, затем введите код.')
      }
      const own = way.kind === 'own' && !cabinetCode ? way.own : null
      return storeDispatch(dispatch)(workplaceMoveRequested({ resourceId, deviceId: mine.deviceId, cashierId: mine.cashierId, credential, own }))
    },
    chooseStartPriceType: (id) => {
      if (vm.start.priceTypes.options.some((option) => option.id === id)) {
        dispatch(startPriceTypeChosen({ resourceId, priceTypeId: id }))
      }
    },
    chooseRegister: (deviceId) => {
      const row = vm.start.registers.rows.find((item) => item.deviceId === deviceId)
      if (row?.selectable === true) dispatch(startRegisterChosen({ resourceId, deviceId }))
    },
    closeSession: () => {
      const session = ownSession()
      if (session !== null) dispatch(sessionCloseRequested(session))
    },
    choosePriceType: (id) => {
      const session = ownSession()
      const option = vm.priceTypes?.options.find((item) => item.id === id)
      if (session === null || option === undefined || option.selected || vm.busy) return
      // Ключ вида — `cash` или `<источник>/<ключ>` (selectors.priceTypeId): GUID источника косой черты не содержит.
      const source = priceTypeRefOf(id)
      dispatch(
        sessionPriceTypeRequested({
          ...session,
          priceTypeResourceId: source?.resourceId ?? null,
          priceTypeKey: source?.key ?? null,
          name: option.name,
        }),
      )
    },
    checkLink: (deviceId) => {
      if (vm.busy) return
      dispatch(linkCheckRequested({ resourceId, deviceId }))
    },
    release: (deviceId, how, reason) => {
      const row = vm.board.rows.find((item) => item.deviceId === deviceId)
      // Чужую сессию служба в списке не называет: пока она не пришла, освободить отсюда нельзя (причина — на экране).
      const foreign = row?.foreignSession ?? null
      if (row === undefined || !row.canRelease || foreign === null || reason.trim() === '') return
      const payload = { resourceId, deviceId, sessionId: foreign.sessionId, expectedVersion: foreign.version, reason: reason.trim() }
      dispatch(how === 'takeover' ? sessionTakeoverRequested(payload) : sessionCloseForeignRequested(payload))
    },
    readShift: () => {
      if (mine !== null) dispatch(rmkReloadRequested({ resourceId, deviceId: mine.deviceId }))
    },
    adoptShift: () => {
      const session = ownSession()
      if (session !== null && vm.shiftCommands.canAdopt) dispatch(shiftAdoptRequested(session))
    },
    openShift: () => {
      const session = ownSession()
      if (session !== null && vm.shiftCommands.canOpen) dispatch(shiftCommandRequested({ ...session, kind: 'open' }))
    },
    closeShift: () => {
      const session = ownSession()
      if (session !== null && vm.shiftCommands.canClose) dispatch(shiftCommandRequested({ ...session, kind: 'close' }))
    },
    checkOperation: () => {
      if (operation !== null) {
        dispatch(
          operationCheckRequested({
            resourceId,
            registerKey: operation.registerKey,
            operationId: operation.operationId,
          }),
        )
      }
    },
    reconcileOperation: () => {
      if (operation?.version == null || !operation.canReconcile) return
      dispatch(
        operationReconcileRequested({
          resourceId,
          registerKey: operation.registerKey,
          operationId: operation.operationId,
          expectedVersion: operation.version,
        }),
      )
    },
    resolveOperation: (outcome, reason, checked) => {
      if (operation?.version == null || !operation.manualRequired) return
      dispatch(
        shiftOperationResolveRequested({
          resourceId,
          registerKey: operation.registerKey,
          operationId: operation.operationId,
          expectedVersion: operation.version,
          outcome,
          reason,
          checked,
        }),
      )
    },
    dismissOperation: () => {
      if (operation !== null && operation.canDismiss) {
        dispatch(
          operationDismissed({ resourceId, registerKey: operation.registerKey, operationId: operation.operationId }),
        )
      }
    },
    dismissFeedback: () => dispatch(feedbackCleared({ key: ridKey(resourceId, FEEDBACK_SCOPES.rmk) })),
    switchSource: navigation.switchSource,
    openJournal: navigation.openJournal,
    closeJournal: navigation.closeJournal,
    openIncidents: navigation.openIncidents,
    openReceipts: navigation.openReceipts,
    openPrintSettings: navigation.openPrintSettings,
    openRefundSearch: navigation.openRefundSearch,
    openSlipRefund: navigation.openSlipRefund,
  }
}

/** Пульс своей работы за кассой — раз в минуту, пока окно РМК открыто и вкладка видна. */
export const HEARTBEAT_INTERVAL_MS = 60_000
